import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../components/PageShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search, MapPin, Briefcase, Loader2, CircleCheck, Target, MessageSquare, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const initials = (name) => name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const Professionals = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ pro_qualifications: [], pro_locations: [] });
  const [search, setSearch] = useState("");
  const [qualification, setQualification] = useState("All");
  const [location, setLocation] = useState("All");

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api
        .get("/professionals", { params: { search: search || undefined, qualification, location } })
        .then((r) => setPros(r.data))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search, qualification, location]);

  const contact = async (p) => {
    if (!user) {
      toast("Please log in to connect", { description: "Create an organisation account to reach professionals." });
      navigate("/login");
      return;
    }
    if (user.role !== "organisation") { toast.error("Only organisations can connect"); return; }
    try {
      const r = await api.post(`/professionals/${p.id}/connect`, {});
      if (r.data.duplicate) toast(`Already connected with ${p.name.split(" ")[0]}`);
      else toast.success(`Request sent to ${p.name.split(" ")[0]}`, { description: "They'll appear in your inbox." });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not connect");
    }
  };

  const messagePro = async (p) => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "organisation") { toast.error("Only organisations can message from here"); return; }
    if (!p.user_id) { toast.error("This demo profile isn't reachable for messaging"); return; }
    try {
      const r = await api.post("/conversations/start", { user_id: p.user_id });
      navigate(`/messages/${r.data.id}`);
    } catch {
      toast.error("Could not start conversation");
    }
  };

  return (
    <PageShell>
      <div className="mx-auto max-w-[1200px] px-6 py-10 lg:px-8">
        <h1 className="font-heading text-[34px] font-extrabold text-[#0B2545] sm:text-[40px]">Registered Professionals</h1>
        <p className="mt-2 text-[16px] text-slate-600">Discover qualified healthcare talent ready to join your team.</p>

        <div className="mt-6 grid grid-cols-1 gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:grid-cols-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, skill..." className="h-11 pl-9" />
          </div>
          <Select value={qualification} onValueChange={setQualification}>
            <SelectTrigger className="h-11"><SelectValue placeholder="Qualification" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All qualifications</SelectItem>
              {meta.pro_qualifications.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger className="h-11"><SelectValue placeholder="Location" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All locations</SelectItem>
              {meta.pro_locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <p className="mt-6 text-sm text-slate-500">{loading ? "Loading..." : `${pros.length} professionals found`}</p>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#16A34A]" /></div>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pros.map((p) => (
              <div key={p.id} className="flex flex-col rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#16A34A]/10 font-heading text-[15px] font-bold text-[#16A34A]">{initials(p.name)}</div>
                  <div>
                    <h3 className="font-heading text-[17px] font-bold leading-tight text-[#0B2545]">{p.name}</h3>
                    <p className="text-sm text-slate-600">{p.title}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-600">
                  <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" />{p.location}</span>
                  <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-slate-400" />{p.experience_years} yrs</span>
                  {p.qualification && <span className="flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-slate-400" />{p.qualification}</span>}
                </div>
                {p.bio && <p className="mt-3 line-clamp-2 text-[14px] leading-relaxed text-slate-600">{p.bio}</p>}
                {p.preferences?.desired_role && (
                  <p className="mt-3 flex items-center gap-1.5 text-sm text-slate-700">
                    <Target className="h-4 w-4 shrink-0 text-[#16A34A]" />
                    <span><span className="font-semibold">Seeking:</span> {p.preferences.desired_role}</span>
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {(p.skills || []).slice(0, 3).map((s) => (
                    <span key={s} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">{s}</span>
                  ))}
                </div>
                {((p.preferences?.preferred_locations || []).length > 0 || (p.preferences?.employment_types || []).length > 0) && (
                  <div className="mt-3 border-t border-slate-100 pt-3">
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Prefers</p>
                    <div className="flex flex-wrap gap-2">
                      {(p.preferences?.employment_types || []).map((t) => (
                        <span key={t} className="rounded-full bg-[#1E4FA3]/10 px-2.5 py-1 text-xs font-medium text-[#1E4FA3]">{t}</span>
                      ))}
                      {(p.preferences?.preferred_locations || []).map((l) => (
                        <span key={l} className="rounded-full bg-[#16A34A]/10 px-2.5 py-1 text-xs font-medium text-[#16A34A]">{l}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-4 flex items-center gap-1.5 text-xs font-medium text-[#16A34A]"><CircleCheck className="h-4 w-4" />{p.availability}</div>
                <div className="mt-4 flex gap-2">
                  <Button onClick={() => contact(p)} className="h-10 flex-1 rounded-lg bg-[#1E4FA3] text-sm font-semibold text-white hover:bg-[#193f83]">Connect</Button>
                  {user?.role === "organisation" && p.user_id && (
                    <Button onClick={() => messagePro(p)} variant="outline" className="h-10 shrink-0 rounded-lg border-2 border-[#1E4FA3]/25 px-3 text-sm font-semibold text-[#1E4FA3] hover:bg-[#1E4FA3]/5">
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {pros.length === 0 && (
              <div className="col-span-full rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center text-slate-500">No professionals match your filters.</div>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default Professionals;
